<template>
  <div>
    <IHTechnicalPlaceSelect
      v-if="siteId != null"
      v-model:technical-category-id="failureNotice.technicalCategoryId"
      v-model:technical-place-id="failureNotice.technicalPlaceId"
      :site-id="siteId"
      :disabled="disableEvent"
    />

    <EventActionFieldRowComponent :label="t('components.issueModal.failure')">
      <span class="p-buttonset">
        <LISAButton
          v-for="box in failureOptions"
          :key="box.text"
          :data-testid="box.id"
          :label="box.text"
          :disabled="disableEvent"
          :class="{ 'p-button-outlined': failureNotice.failure !== box.value }"
          :style="{ width: `${100 / failureOptions.length}%` }"
          @click="failureNotice.failure = box.value"
        />
      </span>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.faultTime')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        v-model:model-value="faultTimeStart"
        required
        :rules="[notFutureDate()]"
        name="faultTimeStart"
      >
        <DatePickerComponentNew
          v-bind="inputField"
          v-model="faultTimeStart"
          class="w-50"
          :clearable="true"
          :range="false"
          :state-as-string="String(valid)"
          :tz="tz"
          :disabled="!failureNotice.failure || disableEvent"
          :state="failureNotice.failure && !disableEvent ? valid : undefined"
          :placeholder="t('license.ih.ih.startDate').toString()"
        />
      </InputRow>
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        :key="failureNotice.faultTimeEnd"
        v-model:model-value="faultTimeEnd"
        :rules="[
          notFutureDate(),
          manualValidation<DateTime | undefined>(isFaultTimeRangeValid, {
            messageKey: 'validation.positiveDateRange',
          }),
        ]"
        name="faultTimeEnd"
      >
        <DatePickerComponentNew
          v-bind="inputField"
          v-model="faultTimeEnd"
          :clearable="true"
          :range="false"
          :state-as-string="String(valid)"
          :tz="tz"
          class="ml-2"
          style="width: calc(50% - 8px) !important"
          :date-disabled-fn="checkDate"
          :state="failureNotice.faultTimeEnd && !disableEvent ? valid : undefined"
          :disabled="!failureNotice.failure || disableEvent"
          :placeholder="t('license.ih.ih.endDate').toString()"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.duration')">
      <InputGroup>
        <div v-if="durationLoading" class="duration-loading p-inputtext p-disabled">
          <ProgressSpinner class="duration-spinner" stroke-width="6" />
        </div>
        <InputText v-else :model-value="duration" disabled></InputText>
        <InputGroupAddon class="p-0">
          {{ t("license.ih.ih.hour") }}
        </InputGroupAddon>
      </InputGroup>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent v-if="showAlert">
      <Message severity="warn">
        {{ t("license.ih.ih.cantArchiveFailureNotice") }}
      </Message>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent v-if="showCost" :label="t('components.issueModal.cost')">
      <InputRow v-slot="{ field: inputField }" v-model.number="failureNotice.cost" name="Cost">
        <InputGroup>
          <LISAInputNumber
            v-bind="inputField"
            v-model="failureNotice.cost"
            :disabled="disableEvent"
          />
          <InputGroupAddon class="p-0">€</InputGroupAddon>
        </InputGroup>
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent v-if="showPriority" :label="t('components.issueModal.priority')">
      <InputRow
        v-slot="{ field: inputField }"
        v-model:model-value="failureNotice.priority"
        name="priority"
      >
        <PrimeVueSelect
          v-bind="inputField"
          v-model="failureNotice.priority"
          :options="priorityOptions"
          option-label="text"
          option-value="value"
          class="w-100"
          :disabled="disableEvent"
          @change="resetPlanDate"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent v-if="showPriority" :label="t('components.issueModal.planDate')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        :model-value="planDate"
        :required="isPlanDate ? true : false"
        name="planDate"
      >
        <DatePickerComponentNew
          v-bind="inputField"
          v-model="planDate"
          :clearable="true"
          :range="true"
          :tz="tz"
          :disabled="!isPlanDate && disableEvent"
          :state="isPlanDate && !disableEvent ? valid : undefined"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <!-- EISENHOWER-PRIORITY -->
    <EventActionFieldRowComponent
      v-if="showEisenhowerPriority === true"
      :label="t('license.ih.ih.eisenhowerPriority')"
    >
      <InputRow v-slot="{ field: inputField }" name="eisenhowerPriority">
        <EisenhowerSelector
          v-bind="inputField"
          v-model="failureNotice.eisenhowerPriority"
          data-testid="fieldsIHFailureEisenhowerPriority"
          :disabled="disableEvent"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.area')">
      <InputRow v-slot="{ field: inputField, validation: { valid } }" name="flags">
        <SearchSelect
          v-bind="inputField"
          v-model="computedFlags"
          :mode="SelectMode.FLAG"
          class="w-100"
          :required="false"
          :filters="{
            type: typesFlagTypes,
            siteIds: siteId,
          }"
          :exclude="excludeFromSearch"
          :disabled="disable.flags || disableEvent"
          :multiple="true"
          :state="valid"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.user')">
      <InputRow
        v-slot="{ field: inputField, validation: { valid } }"
        v-model="failureNotice.ownerId"
        required
        name="ownerId"
      >
        <SearchSelect
          v-bind="inputField"
          v-model="failureNotice.ownerId"
          :mode="SelectMode.USER"
          class="w-100"
          label="preferredName"
          :state="disableEvent ? undefined : valid"
          :filters="
            siteId == null
              ? undefined
              : {
                  hasRightsOnSite: {
                    siteIds: [siteId],
                    rights: SQDC_ASSING_MINIMUM_RIGHTS,
                  },
                }
          "
          :disabled="disable.creator || disableEvent"
          @update:model-value="$emit('assigneeChanged', $event)"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.date')">
      <InputRow v-slot="{ field: inputField }" v-model="deadline" name="deadline">
        <DatePickerComponentNew
          v-bind="inputField"
          v-model="deadline"
          :disabled="disable.date || disableEvent"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <EventActionFieldRowComponent :label="t('components.issueModal.description')">
      <InputRow
        v-slot="{ field: inputField }"
        v-model="failureNotice.description"
        input-id="FieldsIHFailureNoticeModalComponentDescription"
        required
        :rules="[minLength(1)]"
        name="description"
      >
        <PrimeTextArea
          v-bind="inputField"
          v-model="failureNotice.description"
          data-testid="FieldsIHFailureNoticeModalComponentDescription"
          class="w-100"
          auto-resize
          :disabled="disable.description || disableEvent"
          rows="4"
          autofocus
          @keydown.enter="handleEnter"
        />
      </InputRow>
    </EventActionFieldRowComponent>

    <GridRow v-if="showDynamicResponsible || showDynamicDate">
      <GridCol>
        <hr />
        <h3 class="pb-2">
          {{ t("license.ih.components.FieldsIHFailureNoticeModalComponent.preDefinedTask") }}
        </h3>

        <EventActionFieldRowComponent :label="t('components.TaskModal.user')">
          <InputRow
            v-slot="{ field: inputField, validation: { valid } }"
            v-model="failureNotice.dynamicResponsibleId"
            required
            name="dynamicResponsibleId"
          >
            <SearchSelect
              v-bind="inputField"
              v-model="failureNotice.dynamicResponsibleId"
              :mode="SelectMode.USER"
              class="w-100"
              label="preferredName"
              :state="valid"
              :filters="
                siteId == null
                  ? undefined
                  : {
                      hasRightsOnSite: {
                        siteIds: [siteId],
                        rights: SQDC_ASSING_MINIMUM_RIGHTS,
                      },
                    }
              "
              :disable="disableEvent"
            />
          </InputRow>
        </EventActionFieldRowComponent>

        <EventActionFieldRowComponent
          v-if="showDynamicDate && !disableEvent"
          :label="t('components.TaskModal.deadline')"
        >
          <InputRow
            v-slot="{ field: inputField, validation: { valid } }"
            v-model:model-value="failureNotice.dynamicDate"
            required
            name="dynamicDate"
          >
            <DatePickerComponentNew
              v-bind="inputField"
              v-model="dynamicDate"
              class="w-100"
              :state="valid"
              :disable="disableEvent"
              :placeholder="t('components.TaskModal.deadlinePlaceholder')"
              :tz="tz"
            />
          </InputRow>
        </EventActionFieldRowComponent>
      </GridCol>
    </GridRow>
    <slot name="trailing"></slot>
  </div>
</template>

<script setup lang="ts">
import { manualValidation, minLength, notFutureDate } from "@/modules/formValidation";
import InputRow from "@/components/shared/form/InputRow.vue";
import { computed, inject, ref, type PropType } from "vue";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import Message from "primevue/message";
import PrimeVueSelect from "primevue/select";
import PrimeTextArea from "primevue/textarea";
import InputText from "primevue/inputtext";
import ProgressSpinner from "primevue/progressspinner";
import { useStore } from "vuex";
import LISAButton from "@LISAPrime/LISAButton.vue";
import {
  PriorityType,
  EABShorthandTaskDeadline,
  EABShorthandTaskResponsible,
  FlagTypes,
  ShiftDayStatus,
  useGetShiftSchedulesInRangeQuery,
  useGetTechnicalPlaceQuery,
} from "@/graphql";
import DatePickerComponentNew from "@/components/shared/DatePickerComponentNew.vue";
import injectAsserted from "@/functions/assert/injectAsserted";
import {
  CreateInjectionKey,
  DisableEventInjectionKey,
} from "@/components/event-action/issue/IssueTypes";
import { AbstractIssueModalComponentFields } from "@/components/event-action/event/EventHelper";
import IHTechnicalPlaceSelect from "./IHTechnicalPlaceSelect.vue";
import GridRow from "@/components/shared/utility/GridRow.vue";
import GridCol from "@/components/shared/utility/GridCol.vue";
import EventActionFieldRowComponent from "@/components/event-action/EventActionFieldRowComponent.vue";
import type { IHFailureNoticeEditType } from "@/components/event-action/event/modal/EventEditType";
import { TimeZoneInjectionKey } from "@/functions/timezoneUtil";
import LISAInputNumber from "@/components/primevueWrapper/LISAInputNumber.vue";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import EisenhowerSelector from "@/components/event-action/event/eisenhowerMatrix/EisenhowerSelector.vue";
import { useIHConfig, useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import { BoardIdInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { SQDC_ASSING_MINIMUM_RIGHTS } from "@/components/event-action/task/helper/SQDCHelper";
import { useSQDCGlobalSettings } from "@/modules/sqdc/helper/useSQDCGlobalSettings";

const props = defineProps({
  failureNotice: {
    type: Object as PropType<IHFailureNoticeEditType>,
    required: true,
  },
  excludeFromSearch: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  flagTypes: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  disable: {
    type: Object as PropType<Partial<Record<AbstractIssueModalComponentFields, boolean>>>,
    default: () => ({
      description: false,
      owner: false,
      date: false,
      flags: false,
      creator: false,
    }),
  },
});
const emit = defineEmits(["save", "assigneeChanged"]);
const store = useStore();
const { t, n } = useTranslation();
const create = injectAsserted(CreateInjectionKey);
const typesFlagTypes = computed(() => {
  return props.flagTypes as FlagTypes[];
});

const computedFlags = computed<number[]>({
  get() {
    return props.failureNotice.flags.map((flag) => flag.id);
  },
  set(value: number[]) {
    props.failureNotice.flags = value.map((id) => ({ id }));
  },
});
const injectedBoardId = inject(BoardIdInjectionKey, ref<number | null>(null));

const boardId = computed(
  () =>
    props.failureNotice.boardConnection?.find((el) => el.status === 0)?.boardId ??
    props.failureNotice.boardConnection?.[0].boardId ??
    injectedBoardId.value ??
    undefined,
);

const { siteId } = useSpecifiedBoardContext(boardId);

const { resultIHConfig } = useIHConfig(siteId);

const tz = injectAsserted(TimeZoneInjectionKey);

const technicalPlaceVariables = computed(() => ({
  technicalPlaceId: props.failureNotice.technicalPlaceId ?? -1,
}));

const { result: technicalPlaceResult, loading: technicalPlaceLoading } = useGetTechnicalPlaceQuery(
  technicalPlaceVariables,
  {
    enabled: computed(() => props.failureNotice.technicalPlaceId != null),
  },
);

const costCenterId = computed(() => {
  const technicalPlace = technicalPlaceResult.value?.GetTechnicalPlace;
  if (technicalPlace == null || technicalPlace.id !== props.failureNotice.technicalPlaceId) {
    return undefined;
  }
  return technicalPlace.costCenterId;
});

const shiftScheduleVariables = computed(() => ({
  start:
    DateTime.fromISO(props.failureNotice.faultTimeStart)
      .setZone(tz.value)
      .minus({ days: 1 })
      .toISODate() ?? "",
  end:
    DateTime.fromISO(props.failureNotice.faultTimeEnd ?? props.failureNotice.faultTimeStart)
      .setZone(tz.value)
      .plus({ days: 1 })
      .toISODate() ?? "",
  ccId: costCenterId.value ?? -1,
}));

const { result: shiftScheduleResult, loading: shiftScheduleLoading } =
  useGetShiftSchedulesInRangeQuery(shiftScheduleVariables, {
    enabled: computed(
      () =>
        costCenterId.value != null &&
        props.failureNotice.faultTimeEnd != null &&
        DateTime.fromISO(props.failureNotice.faultTimeStart).isValid &&
        DateTime.fromISO(props.failureNotice.faultTimeEnd).isValid,
    ),
  });

const planDate = computed<[DateTime | undefined, DateTime | undefined]>({
  get() {
    return props.failureNotice.planDate?.length
      ? [
          DateTime.fromISO(props.failureNotice.planDate?.[0]),
          DateTime.fromISO(props.failureNotice.planDate?.[1]),
        ]
      : [undefined, undefined];
  },
  set(value) {
    if (value == null || value.some((el) => el == null)) {
      props.failureNotice.planDate = undefined;
    }
    props.failureNotice.planDate = value.map((el) => el!.toISO());
  },
});

const dynamicDate = computed({
  get() {
    if (props.failureNotice.dynamicDate == null) {
      return undefined;
    }
    return DateTime.fromISO(props.failureNotice.dynamicDate);
  },
  set(val) {
    props.failureNotice.dynamicDate = val?.toISO();
  },
});

const showDynamicDate = computed(
  () =>
    create.value &&
    resultIHConfig.value?.eabShorthand?.taskComponent.deadlineType ===
      EABShorthandTaskDeadline.DYNAMIC,
);

const showDynamicResponsible = computed(
  () =>
    create.value &&
    (resultIHConfig.value?.eabShorthand?.taskComponent.responsible ===
      EABShorthandTaskResponsible.DYNAMIC ||
      (resultIHConfig.value?.eabShorthand?.taskComponent.responsible ===
        EABShorthandTaskResponsible.SELF &&
        store.getters["login/isAnonym"])),
);

const isPlanDate = computed(() => props.failureNotice.priority === PriorityType.planDate);

const faultTimeStart = computed<DateTime>({
  get() {
    return DateTime.fromISO(props.failureNotice.faultTimeStart);
  },
  set(value) {
    props.failureNotice.faultTimeStart = value.toISO();
  },
});

const faultTimeEnd = computed<DateTime | undefined>({
  get() {
    return props.failureNotice.faultTimeEnd
      ? DateTime.fromISO(props.failureNotice.faultTimeEnd)
      : undefined;
  },
  set(value) {
    props.failureNotice.faultTimeEnd = value ? value.toISO() : undefined;
  },
});

const isFaultTimeRangeValid = computed(() => {
  if (!props.failureNotice.failure || faultTimeEnd.value == null) {
    return true;
  }

  return (
    faultTimeStart.value.isValid &&
    faultTimeEnd.value.isValid &&
    faultTimeEnd.value >= faultTimeStart.value
  );
});

function checkDate(date: Date) {
  if (
    date.setHours(0, 0, 0, 0).valueOf() >=
    faultTimeStart.value
      .set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
      .valueOf()
  ) {
    return false;
  }
  return true;
}
const priorityOptions = computed(() => [
  {
    text: t("components.tiles.IssueModalForTiles.bis.priority.date"),
    value: PriorityType.planDate,
  },
  {
    text: t("components.tiles.IssueModalForTiles.bis.priority.prio0"),
    value: PriorityType.immediately,
  },
  {
    text: t("components.tiles.IssueModalForTiles.bis.priority.prio1"),
    value: PriorityType.sameDay,
  },
  {
    text: t("components.tiles.IssueModalForTiles.bis.priority.prio2"),
    value: PriorityType.urgent,
  },
  {
    text: t("components.tiles.IssueModalForTiles.bis.priority.prio3"),
    value: PriorityType.standard,
  },
  {
    text: t("components.tiles.IssueModalForTiles.bis.priority.prio4"),
    value: PriorityType.lowRisk,
  },
  {
    text: t("components.tiles.IssueModalForTiles.bis.priority.prio5"),
    value: PriorityType.littleRisk,
  },
  {
    text: t("components.tiles.IssueModalForTiles.bis.priority.prio6"),
    value: PriorityType.mediumTerm,
  },
  {
    text: t("components.tiles.IssueModalForTiles.bis.priority.prio7"),
    key: PriorityType.longTerm,
  },
]);

const failureOptions = computed(() => [
  {
    text: t("components.tiles.IssueModalForTiles.bis.noFailure"),
    value: false,
    id: "noFailure-button",
  },
  {
    text: t("components.tiles.IssueModalForTiles.bis.failure"),
    value: true,
    id: "failure-button",
  },
]);

const showAlert = computed(() => {
  return props.failureNotice.failure && !props.failureNotice.faultTimeEnd;
});

const resetPlanDate = () => {
  props.failureNotice.planDate = undefined;
};

function calculateDuration(start: string, end: string) {
  const startMillis = DateTime.fromISO(start).toMillis();
  const endMillis = DateTime.fromISO(end).toMillis();
  if (
    endMillis <= startMillis ||
    costCenterId.value == null ||
    shiftScheduleResult.value == null ||
    shiftScheduleResult.value?.CostCenters[0]?.id !== costCenterId.value
  ) {
    return Number(((endMillis - startMillis) / 3_600_000).toFixed(2));
  }

  const intervals = shiftScheduleResult.value.GetShiftSchedulesInRange[0].shiftsInRange
    .filter(
      (shift) => shift.type !== ShiftDayStatus.CANCELED && shift.from != null && shift.to != null,
    )
    .map((shift) => ({
      start: Math.max(DateTime.fromISO(shift.from!).toMillis(), startMillis),
      end: Math.min(DateTime.fromISO(shift.to!).toMillis(), endMillis),
    }))
    .filter((interval) => interval.end > interval.start)
    .sort((a, b) => a.start - b.start);

  if (intervals.length === 0) {
    return shiftScheduleResult.value.GetShiftSchedulesInRange[0].shiftSchedules.length > 0
      ? 0
      : Number(((endMillis - startMillis) / 3_600_000).toFixed(2));
  }

  let durationMillis = 0;
  let currentStart = intervals[0]?.start;
  let currentEnd = intervals[0]?.end;

  for (const interval of intervals.slice(1)) {
    if (currentEnd == null || currentStart == null) {
      break;
    }
    if (interval.start <= currentEnd) {
      currentEnd = Math.max(currentEnd, interval.end);
    } else {
      durationMillis += currentEnd - currentStart;
      currentStart = interval.start;
      currentEnd = interval.end;
    }
  }

  if (currentStart != null && currentEnd != null) {
    durationMillis += currentEnd - currentStart;
  }

  return Number((durationMillis / 3_600_000).toFixed(2));
}

const durationLoading = computed(() => {
  if (
    props.failureNotice.faultTimeEnd == null ||
    !faultTimeStart.value.isValid ||
    faultTimeEnd.value == null ||
    !faultTimeEnd.value.isValid ||
    faultTimeEnd.value <= faultTimeStart.value
  ) {
    return false;
  }
  if (
    props.failureNotice.technicalPlaceId != null &&
    (technicalPlaceLoading.value ||
      technicalPlaceResult.value?.GetTechnicalPlace?.id !== props.failureNotice.technicalPlaceId)
  ) {
    return true;
  }
  return (
    costCenterId.value != null &&
    (shiftScheduleLoading.value ||
      shiftScheduleResult.value == null ||
      shiftScheduleResult.value.CostCenters[0]?.id !== costCenterId.value)
  );
});

const duration = computed(() => {
  if (durationLoading.value) {
    return "";
  }
  return props.failureNotice.faultTimeEnd
    ? n(calculateDuration(props.failureNotice.faultTimeStart, props.failureNotice.faultTimeEnd))
    : "0";
});

const showCost = computed(() => {
  return !resultIHConfig.value || resultIHConfig?.value?.showCost;
});

const showPriority = computed(() => {
  return !resultIHConfig.value || resultIHConfig?.value?.showPriority;
});

const deadline = computed<DateTime>({
  get() {
    return DateTime.fromJSDate(new Date(props.failureNotice.deadline));
  },
  set(val) {
    props.failureNotice.deadline = val.toISO();
  },
});

const disableEvent = injectAsserted(DisableEventInjectionKey);

function handleEnter(e: KeyboardEvent) {
  if (e.metaKey || e.ctrlKey) {
    emit("save", e.shiftKey);
  }
}

const { showEisenhowerPriority } = useSQDCGlobalSettings();
</script>

<style lang="scss" scoped>
.pointer-event-none {
  pointer-events: none;
}

.border-danger {
  border-color: var(--p-red-500);
}

.duration-loading {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
}

.duration-spinner {
  height: 1.25rem;
  width: 1.25rem;
}

.border-success {
  border-color: var(--p-success);
}
</style>
